import { Location, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDatepickerModule, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { DatePickerComponent } from 'components/date-picker/date-picker.component';
import { MapModalComponent } from 'components/modal/map-modal/map-modal.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TimeInputComponent } from 'components/time-input/time-input.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { CreateReadingRoomRequest, ReadingRoomCard, UpdateReadingRoomRequest } from 'types/reading-room.interface';
import { StructuralUnitListItem, StructuralUnitListItemPagedResponse } from 'types/structural-unit.interface';
import { formatDate } from 'util/date.utility';
import {
  MAX_LENGTH_ADDRESS,
  MAX_LENGTH_CODE,
  MAX_LENGTH_DESCRIPTION,
  MAX_LENGTH_EMAIL,
  MAX_LENGTH_NAME,
  MAX_LENGTH_PHONE,
  MIN_LENGTH_ADDRESS,
  MIN_LENGTH_EMAIL,
  MIN_LENGTH_PHONE,
  emailValidator,
  getControlsWithErrors,
  phoneValidator,
  requiredIfNotHolidayValidator,
  startTimeBeforeEndTimeValidator,
  structuralUnitSelectedValidator
} from 'util/form.utility';
import { sortSchedule } from 'util/reading-room.utility';
import { ConfirmModalComponent } from '../../../components/modal/confirm-modal/confirm-modal.component';
import { checkClaims } from 'util/user.utility';
import { CLAIM_SCOPES, CLAIM_TYPES } from 'types/user.interface';

@Component({
  selector: 'app-reading-room-form-page',
  standalone: true,
  imports: [
    TranslateModule,
    FormsModule,
    NgbDatepickerModule,
    NgbModule,
    MapModalComponent,
    DatePickerComponent,
    ReactiveFormsModule,
    NgClass,
    SpinnerComponent,
    BreadcrumbsComponent,
    TimeInputComponent,
    ConfirmModalComponent,
    TooltipComponent
  ],
  templateUrl: './reading-room-form-page.component.html',
  styleUrl: './reading-room-form-page.component.scss'
})
export class ReadingRoomFormPageComponent implements OnInit {
  public readonly formatDate = formatDate;
  public readonly CLAIM_SCOPES = CLAIM_SCOPES;
  public readonly CLAIM_TYPES = CLAIM_TYPES;
  public readonly checkClaims = checkClaims;
  public readonly maxFieldLength = MAX_LENGTH_DESCRIPTION;
  breadcrumbs: Breadcrumb[] = [
    {
      title: 'components.breadcrumbs.readingRooms',
      url: '/lasitavas'
    }
  ];

  days: string[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  readingRoomId: string | null = null;
  readingRoom?: ReadingRoomCard;
  structuralUnits: StructuralUnitListItem[] = [];
  isReadingRoomLoading: boolean = true;
  isStructuralUnitsLoading: boolean = true;
  isSubmitLoading: boolean = false;
  submitAttempted: boolean = false;
  openModalIndex: number | null = null;
  isOpenConfirmDeleteRoom: boolean = false;
  isOpenConfirmDeleteNotif: boolean = false;
  isOpenConfirmBack: boolean = false;
  get isLoading(): boolean {
    return (this.isReadingRoomLoading && !!this.readingRoomId) || this.isStructuralUnitsLoading || this.isSubmitLoading;
  }
  form: FormGroup;

  get name(): AbstractControl<any, any> | null {
    return this.form.get('name');
  }
  get nameEn(): AbstractControl<any, any> | null {
    return this.form.get('nameEn');
  }
  get address(): AbstractControl<any, any> | null {
    return this.form.get('address');
  }
  get latitude(): AbstractControl<any, any> | null {
    return this.form.get('latitude');
  }
  get longitude(): AbstractControl<any, any> | null {
    return this.form.get('longitude');
  }
  get contactPhone(): AbstractControl<any, any> | null {
    return this.form.get('contactPhone');
  }
  get email(): AbstractControl<any, any> | null {
    return this.form.get('email');
  }
  get imageUrl(): AbstractControl<any, any> | null {
    return this.form.get('imageUrl');
  }
  get applicationEmail(): AbstractControl<any, any> | null {
    return this.form.get('applicationEmail');
  }
  get description(): AbstractControl<any, any> | null {
    return this.form.get('description');
  }
  get descriptionEn(): AbstractControl<any, any> | null {
    return this.form.get('descriptionEn');
  }
  // get documentAvailability(): AbstractControl<any, any> | null {
  //   return this.form.get('documentAvailability');
  // }
  // get documentAvailabilityEn(): AbstractControl<any, any> | null {
  //   return this.form.get('documentAvailabilityEn');
  // }
  get scheduleEntries(): FormArray {
    return this.form.get('scheduleEntries') as FormArray;
  }
  get notificationEntries(): FormArray {
    return this.form.get('notificationEntries') as FormArray;
  }
  get structuralUnitId(): AbstractControl<any, any> | null {
    return this.form.get('structuralUnitId');
  }
  get code(): AbstractControl<any, any> | null {
    return this.form.get('code');
  }

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private router: Router,
    private toastService: ToastService
  ) {
    this.form = this.formBuilder.group({});
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.readingRoomId = params['id'];

      if (!this.readingRoomId) {
        this.breadcrumbs.push({
          title: 'components.breadcrumbs.addReadingRoom',
          url: '/lasitavas/pievienot'
        });
        this.fillForm();
      }

      this.fetchData();
    });
  }

  fetchData(): void {
    this.apiService.getStructuralUnits().subscribe(
      (data: StructuralUnitListItemPagedResponse) => {
        this.structuralUnits = data.list ? data.list : [];
        this.isStructuralUnitsLoading = false;
      },
      (error: any) => {
        console.error(error);
      }
    );

    if (this.readingRoomId) {
      this.apiService.getReadingRoom(this.readingRoomId).subscribe(
        (data: any) => {
          this.readingRoom = data;

          if (this.readingRoom?.scheduleEntries) {
            this.readingRoom.scheduleEntries = sortSchedule(this.readingRoom?.scheduleEntries || []);
          }

          this.isReadingRoomLoading = false;
          this.fillForm();

          this.breadcrumbs.push({
            title: 'components.breadcrumbs.editReadingRoom',
            titleArgs: { name: this.readingRoom?.name },
            url: `/lasitavas/${this.readingRoomId}/rediget`
          });
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      name: [this.readingRoom?.name || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      nameEn: [this.readingRoom?.nameEn || '', [Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]],
      address: [
        this.readingRoom?.address || '',
        [Validators.required, Validators.minLength(MIN_LENGTH_ADDRESS), Validators.maxLength(MAX_LENGTH_ADDRESS)]
      ],
      latitude: [this.readingRoom?.latitude, [Validators.required]],
      longitude: [this.readingRoom?.longitude, [Validators.required]],
      contactPhone: [
        this.readingRoom?.contactPhone || '',
        [Validators.required, phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]
      ],
      imageUrl: [this.readingRoom?.imageUrl || ''],
      email: [
        this.readingRoom?.email || '',
        [Validators.required, emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
      ],
      applicationEmail: [
        this.readingRoom?.applicationEmail || '',
        [Validators.required, emailValidator(), Validators.minLength(MIN_LENGTH_EMAIL), Validators.maxLength(MAX_LENGTH_EMAIL)]
      ],
      description: [this.readingRoom?.description || '', [Validators.required, Validators.maxLength(MAX_LENGTH_DESCRIPTION)]],
      // documentAvailability: [this.readingRoom?.documentAvailability || '', [Validators.maxLength(MAX_LENGTH_DESCRIPTION)]],
      descriptionEn: [this.readingRoom?.descriptionEn || '', [Validators.maxLength(MAX_LENGTH_DESCRIPTION)]],
      // documentAvailabilityEn: [this.readingRoom?.documentAvailabilityEn || '', [Validators.maxLength(MAX_LENGTH_DESCRIPTION)]],
      scheduleEntries: this.formBuilder.array(
        this.readingRoom?.scheduleEntries
          ? this.readingRoom.scheduleEntries.map((entry: any) =>
              this.formBuilder.group(entry, {
                validators: [startTimeBeforeEndTimeValidator, requiredIfNotHolidayValidator]
              })
            )
          : this.days.map((day) => this.createDefaultScheduleEntry(day))
      ),
      notificationEntries: this.formBuilder.array(
        this.readingRoom?.notificationEntries ? this.readingRoom.notificationEntries.map((entry: any) => this.formBuilder.group(entry)) : []
      ),
      structuralUnitId: [this.readingRoom?.structuralUnit?.id || -1, [Validators.required, structuralUnitSelectedValidator()]],
      code: [this.readingRoom?.code || '', [Validators.required, Validators.maxLength(MAX_LENGTH_CODE)]]
    });
  }

  addNotification() {
    this.notificationEntries.push(
      this.formBuilder.group({
        text: ['', Validators.required],
        textEn: ['', Validators.required],
        startDate: [null, Validators.required],
        endDate: null
      })
    );
    this.form.markAsDirty();
  }

  removeNotification(index: number | null) {
    this.form.markAsDirty();

    if (index !== null) {
      this.openModalIndex = null;
      this.notificationEntries.removeAt(index);
    }
  }

  onNotificationDateSelected(event: string, index: number, key: string) {
    const notificationEntry = this.notificationEntries.at(index) as FormGroup;
    const value = event.length > 0 ? event : null;

    if (!notificationEntry.get(key)) {
      notificationEntry.addControl(key, this.formBuilder.control(value));
    } else {
      notificationEntry.get(key)?.setValue(value);
      this.form.markAsDirty();
    }
  }

  setCoordinates(coordinates: any) {
    this.form.patchValue({
      latitude: coordinates.lat,
      longitude: coordinates.lng
    });
  }

  getFormattedCoordinates(): string {
    const latitude = this.form.get('latitude')?.value;
    const longitude = this.form.get('longitude')?.value;

    return latitude && longitude ? `${latitude}, ${longitude}` : '';
  }

  onTimeChange(event: string | null, index: number, key: string): void {
    this.scheduleEntries.at(index).get(key)?.setValue(event);
    this.form.markAsDirty();
  }

  createDefaultScheduleEntry(day: string): FormGroup {
    return this.formBuilder.group(
      {
        day: day,
        startTime: '00:00:00',
        endTime: '00:00:00',
        isDayOff: false
      },
      {
        validators: [startTimeBeforeEndTimeValidator, requiredIfNotHolidayValidator]
      }
    );
  }

  submit(): void {
    this.submitAttempted = true;

    if (this.form.valid) {
      this.isSubmitLoading = true;
      if (this.readingRoomId) {
        const formData: UpdateReadingRoomRequest = {
          id: this.readingRoomId,
          ...this.form.value
        };

        this.apiService.updateReadingRoom(formData).subscribe(
          () => {
            this.router.navigate(['/lasitavas']);
            this.isSubmitLoading = false;
            this.toastService.showSuccess('editReadingRoomSuccess', '', {
              shouldTranslate: true
            });
          },
          (error: any) => {
            console.error(error);
            this.isSubmitLoading = false;
          }
        );
      } else {
        const formData: CreateReadingRoomRequest = this.form.value;

        this.apiService.createReadingRoom(formData).subscribe(
          () => {
            this.router.navigate(['/lasitavas']);
            this.isSubmitLoading = false;
            this.toastService.showSuccess('addReadingRoomSuccess', '', {
              shouldTranslate: true
            });
          },
          (error: any) => {
            console.error(error);
            this.isSubmitLoading = false;
          }
        );
      }
    } else {
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'pages.readingRoom');
    }
  }

  openConfirmModal(): void {
    this.isOpenConfirmDeleteRoom = true;
  }

  onModalClose(): void {
    this.isOpenConfirmDeleteRoom = false;
    this.isOpenConfirmDeleteNotif = false;
    this.isOpenConfirmBack = false;
  }

  openDeleteNotificationModal(index: number | null): void {
    if (index !== null) {
      this.openModalIndex = index;
      this.isOpenConfirmDeleteNotif = true;
    }
  }

  delete(): void {
    if (this.readingRoomId) {
      this.isSubmitLoading = true;

      this.apiService.deleteReadingRoom(this.readingRoomId).subscribe(
        () => {
          this.router.navigate(['/lasitavas']);
          this.toastService.showSuccess('deleteReadingRoomSuccess', '', { shouldTranslate: true });
          this.isSubmitLoading = false;
        },
        (error: any) => {
          this.isSubmitLoading = false;
          console.error(error);
        }
      );
    }
  }

  back(confirmed: boolean = false): void {
    if (!this.hasFormChanged() || confirmed) {
      this.location.back();
    } else {
      this.isOpenConfirmBack = true;
    }
  }

  shouldRenderError(key: string, index: number): boolean {
    if (this.submitAttempted) {
      return true;
    }

    if (key === 'scheduleEntries') {
      const scheduleEntry = this.scheduleEntries.at(index) as FormGroup;
      const timeInputsTouched = scheduleEntry.get('startTime')?.touched && scheduleEntry.get('endTime')?.touched;
      return scheduleEntry.invalid && !!timeInputsTouched && !!scheduleEntry.errors;
    } else if (key.startsWith('notificationEntries.')) {
      const groupKey = key.split('.')[1];
      const notificationEntry = this.notificationEntries.at(index) as FormGroup;
      const field = notificationEntry.get(groupKey);

      return !!field?.invalid && !!field?.touched && !!field?.errors;
    } else {
      const control = this.form.get(key);

      if (control) {
        return control.invalid && control.touched && !!control?.errors;
      }
    }

    return false;
  }

  onStructuralUnitBlur(): void {
    const control = this.form.get('structuralUnitId');

    if (control) {
      control.markAsTouched();
    }
  }

  markScheduleInputAsTouched(index: number, key: string): void {
    const control = this.scheduleEntries.at(index) as FormGroup;
    control.get(key)?.markAsTouched();
  }

  markNotificationDateAsTouched(index: number, key: string): void {
    const control = this.notificationEntries.at(index) as FormGroup;
    control.get(key)?.markAsTouched();
  }

  hasFormChanged(): boolean {
    return this.form.dirty || this.haveCoordsChanged();
  }

  haveCoordsChanged(): boolean {
    return (
      this.readingRoom?.latitude !== this.form.get('latitude')?.value || this.readingRoom?.longitude !== this.form.get('longitude')?.value
    );
  }
}
